// src/Evraklarim/evraklarim.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  AvatarGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Skeleton,
  Alert,
  Fab,
  Zoom,
  Fade,
  Slide,
  Grow,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  styled
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  FolderShared as FolderSharedIcon
} from '@mui/icons-material';
import { styled as muiStyled } from '@mui/material/styles';
import { format } from 'date-fns';
import { tr, enUS, de } from 'date-fns/locale';

// ==================== RENK PALETİ ====================
const COLORS = {
  primary: '#f57c00',    // Turuncu
  secondary: '#2563EB',  // Mavi
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  background: '#f8fafc',
  surface: '#ffffff',
  text: {
    primary: '#0f172a',
    secondary: '#64748b',
    hint: '#94a3b8'
  },
  border: '#e2e8f0'
};

// ==================== STYLED COMPONENTS ====================
const StyledCard = muiStyled(Card)(({ theme }) => ({
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(245, 124, 0, 0.12)',
    '& .card-actions': {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  '&:hover::before': {
    opacity: 1
  }
}));

const GradientHeader = muiStyled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  color: 'white',
  borderRadius: 24,
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 200,
    height: 200,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    pointerEvents: 'none'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -50,
    left: -50,
    width: 150,
    height: 150,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    pointerEvents: 'none'
  }
}));

const StatusChip = muiStyled(Chip)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: 30,
  padding: '4px 8px',
  height: 32,
  backgroundColor: statuscolor === 'warning' ? '#fff3e0' : 
                  statuscolor === 'success' ? '#e8f5e8' :
                  statuscolor === 'error' ? '#ffebee' : 
                  statuscolor === 'info' ? '#e3f2fd' : '#f3e5f5',
  color: statuscolor === 'warning' ? '#ed6c02' :
         statuscolor === 'success' ? '#2e7d32' :
         statuscolor === 'error' ? '#d32f2f' : 
         statuscolor === 'info' ? '#0288d1' : '#9c27b0',
  border: 'none',
  '& .MuiChip-icon': {
    color: 'inherit'
  },
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8
  }
}));

const ActionButton = muiStyled(IconButton)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.1)',
    boxShadow: '0 6px 16px rgba(245, 124, 0, 0.2)'
  }
}));

const FloatingActionButton = muiStyled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: 'white',
  boxShadow: '0 8px 24px rgba(245, 124, 0, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 12px 32px rgba(245, 124, 0, 0.4)'
  },
  '@media (max-width: 600px)': {
    bottom: 16,
    right: 16
  }
}));

const AnimatedCard = motion(StyledCard);

// ==================== DİL DOSYALARI ====================
const translations = {
  tr: {
    pageTitle: 'Evraklarım',
    pageSubtitle: 'Tüm belgelerinizi tek bir yerden yönetin',
    searchPlaceholder: 'Ara: evrak adı, tarih, kategori...',
    filter: 'Filtrele',
    refresh: 'Yenile',
    newDocument: 'Yeni Evrak',
    uploadTemplate: 'Şablon Ekle',
    tabs: {
      all: 'Tümü',
      pending: 'Bekleyen',
      completed: 'Tamamlanan',
      draft: 'Taslak',
      archived: 'Arşiv'
    },
    stats: {
      total: 'Toplam Evrak',
      pending: 'İmza Bekleyen',
      completed: 'Tamamlanan',
      draft: 'Taslak'
    },
    card: {
      created: 'Oluşturulma',
      deadline: 'Son Tarih',
      sender: 'Gönderen',
      pages: 'sayfa',
      view: 'Görüntüle',
      download: 'İndir',
      share: 'Paylaş',
      more: 'Daha Fazla'
    },
    status: {
      pending: 'İmza Bekliyor',
      signed: 'İmzalandı',
      rejected: 'Reddedildi',
      viewed: 'Görüntülendi',
      draft: 'Taslak',
      archived: 'Arşivlendi'
    },
    empty: {
      title: 'Henüz evrak bulunmuyor',
      description: 'Yeni bir evrak oluşturmak için butona tıklayın',
      action: 'Evrak Oluştur'
    },
    dialog: {
      delete: {
        title: 'Evrakı Sil',
        message: 'Bu evrakı silmek istediğinize emin misiniz?',
        confirm: 'Sil',
        cancel: 'İptal'
      },
      share: {
        title: 'Evrakı Paylaş',
        email: 'E-posta Adresi',
        message: 'Mesaj',
        send: 'Gönder'
      }
    },
    notifications: {
      deleted: 'Evrak başarıyla silindi',
      shared: 'Evrak paylaşıldı',
      error: 'Bir hata oluştu'
    }
  },
  en: {
    pageTitle: 'My Documents',
    pageSubtitle: 'Manage all your documents in one place',
    searchPlaceholder: 'Search: document name, date, category...',
    filter: 'Filter',
    refresh: 'Refresh',
    newDocument: 'New Document',
    uploadTemplate: 'Add Template',
    tabs: {
      all: 'All',
      pending: 'Pending',
      completed: 'Completed',
      draft: 'Draft',
      archived: 'Archived'
    },
    stats: {
      total: 'Total Documents',
      pending: 'Pending Signature',
      completed: 'Completed',
      draft: 'Draft'
    },
    card: {
      created: 'Created',
      deadline: 'Deadline',
      sender: 'Sender',
      pages: 'pages',
      view: 'View',
      download: 'Download',
      share: 'Share',
      more: 'More'
    },
    status: {
      pending: 'Pending Signature',
      signed: 'Signed',
      rejected: 'Rejected',
      viewed: 'Viewed',
      draft: 'Draft',
      archived: 'Archived'
    },
    empty: {
      title: 'No documents yet',
      description: 'Click the button to create a new document',
      action: 'Create Document'
    },
    dialog: {
      delete: {
        title: 'Delete Document',
        message: 'Are you sure you want to delete this document?',
        confirm: 'Delete',
        cancel: 'Cancel'
      },
      share: {
        title: 'Share Document',
        email: 'Email Address',
        message: 'Message',
        send: 'Send'
      }
    },
    notifications: {
      deleted: 'Document deleted successfully',
      shared: 'Document shared',
      error: 'An error occurred'
    }
  },
  de: {
    pageTitle: 'Meine Dokumente',
    pageSubtitle: 'Verwalten Sie alle Ihre Dokumente an einem Ort',
    searchPlaceholder: 'Suchen: Dokumentname, Datum, Kategorie...',
    filter: 'Filtern',
    refresh: 'Aktualisieren',
    newDocument: 'Neues Dokument',
    uploadTemplate: 'Vorlage Hinzufügen',
    tabs: {
      all: 'Alle',
      pending: 'Ausstehend',
      completed: 'Abgeschlossen',
      draft: 'Entwurf',
      archived: 'Archiviert'
    },
    stats: {
      total: 'Dokumente Gesamt',
      pending: 'Ausstehende Unterschrift',
      completed: 'Abgeschlossen',
      draft: 'Entwurf'
    },
    card: {
      created: 'Erstellt',
      deadline: 'Frist',
      sender: 'Absender',
      pages: 'Seiten',
      view: 'Ansehen',
      download: 'Herunterladen',
      share: 'Teilen',
      more: 'Mehr'
    },
    status: {
      pending: 'Unterschrift ausstehend',
      signed: 'Unterschrieben',
      rejected: 'Abgelehnt',
      viewed: 'Angesehen',
      draft: 'Entwurf',
      archived: 'Archiviert'
    },
    empty: {
      title: 'Keine Dokumente vorhanden',
      description: 'Klicken Sie auf die Schaltfläche, um ein neues Dokument zu erstellen',
      action: 'Dokument Erstellen'
    },
    dialog: {
      delete: {
        title: 'Dokument Löschen',
        message: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
        confirm: 'Löschen',
        cancel: 'Abbrechen'
      },
      share: {
        title: 'Dokument Teilen',
        email: 'E-Mail-Adresse',
        message: 'Nachricht',
        send: 'Senden'
      }
    },
    notifications: {
      deleted: 'Dokument erfolgreich gelöscht',
      shared: 'Dokument geteilt',
      error: 'Ein Fehler ist aufgetreten'
    }
  }
};

// ==================== ANA BİLEŞEN ====================
export default function Evraklarim() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const navigate = useNavigate();
  const notify = useNotify();

  // Dil
  const [lang, setLang] = useState('tr');
  const t = translations[lang] || translations.tr;

  // State'ler
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    draft: 0
  });

  // Mock veri (API'den gelecek)
  useEffect(() => {
    setTimeout(() => {
      const mockData = [
        {
          id: 1,
          title: 'Vekaletname - Musteri A',
          description: 'Sigorta vekaletnamesi',
          status: 'pending',
          createdAt: '2026-03-01',
          deadline: '2026-03-15',
          sender: 'Mern net GmbH',
          pages: 2,
          size: '1.2 MB',
          recipients: [
            { name: 'Ahmet Yılmaz', status: 'pending' },
            { name: 'Mehmet Demir', status: 'signed' }
          ],
          tags: ['vekalet', 'sigorta'],
          starred: true
        },
        {
          id: 2,
          title: 'Hizmet Sözleşmesi',
          description: 'Danışmanlık hizmetleri',
          status: 'signed',
          createdAt: '2026-02-28',
          deadline: '2026-03-10',
          sender: 'Mern net GmbH',
          pages: 5,
          size: '2.5 MB',
          recipients: [
            { name: 'Ayşe Kaya', status: 'signed' }
          ],
          tags: ['sözleşme', 'danışmanlık'],
          starred: false
        },
        {
          id: 3,
          title: 'Teklif Formu - Generali',
          description: 'Araç sigortası teklifi',
          status: 'draft',
          createdAt: '2026-03-05',
          deadline: '2026-03-20',
          sender: 'Generali Sigorta',
          pages: 3,
          size: '0.8 MB',
          recipients: [],
          tags: ['teklif', 'sigorta'],
          starred: true
        },
        {
          id: 4,
          title: 'Müşteri Bilgi Formu',
          description: 'Kişisel bilgiler',
          status: 'viewed',
          createdAt: '2026-02-25',
          deadline: '2026-03-05',
          sender: 'Mern net GmbH',
          pages: 1,
          size: '0.3 MB',
          recipients: [
            { name: 'Ali Öztürk', status: 'viewed' }
          ],
          tags: ['form', 'müşteri'],
          starred: false
        },
        {
          id: 5,
          title: 'Mahkeme Kararı - 2026/123',
          description: 'İcra takibi',
          status: 'archived',
          createdAt: '2026-01-15',
          deadline: '2026-02-15',
          sender: 'İstanbul Adliyesi',
          pages: 8,
          size: '3.1 MB',
          recipients: [],
          tags: ['mahkeme', 'hukuk'],
          starred: false
        }
      ];

      setDocuments(mockData);
      filterDocuments(mockData, searchQuery, currentTab);
      calculateStats(mockData);
      setLoading(false);
    }, 1500);
  }, []);

  // İstatistik hesapla
  const calculateStats = (docs) => {
    setStats({
      total: docs.length,
      pending: docs.filter(d => d.status === 'pending').length,
      completed: docs.filter(d => d.status === 'signed').length,
      draft: docs.filter(d => d.status === 'draft').length
    });
  };

  // Filtreleme
  const filterDocuments = (docs, query, tab) => {
    let filtered = [...docs];

    // Arama filtresi
    if (query) {
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(query.toLowerCase()) ||
        doc.description.toLowerCase().includes(query.toLowerCase()) ||
        doc.sender.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Tab filtresi
    if (tab === 1) { // Bekleyen
      filtered = filtered.filter(doc => doc.status === 'pending');
    } else if (tab === 2) { // Tamamlanan
      filtered = filtered.filter(doc => doc.status === 'signed');
    } else if (tab === 3) { // Taslak
      filtered = filtered.filter(doc => doc.status === 'draft');
    } else if (tab === 4) { // Arşiv
      filtered = filtered.filter(doc => doc.status === 'archived');
    }

    setFilteredDocs(filtered);
  };

  // Arama değişikliği
  useEffect(() => {
    filterDocuments(documents, searchQuery, currentTab);
  }, [searchQuery, currentTab, documents]);

  // Menü işlemleri
  const handleMenuOpen = (event, doc) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const handleView = (doc) => {
    navigate(`/evrak/${doc.id}`);
    handleMenuClose();
  };

  const handleDownload = (doc) => {
    window.open(`/api/documents/${doc.id}/download`, '_blank');
    handleMenuClose();
  };

  const handleShare = (doc) => {
    setShareDialog(true);
    handleMenuClose();
  };

  const handleDelete = (doc) => {
    setDeleteDialog(true);
    handleMenuClose();
  };

  const confirmDelete = () => {
    setDocuments(docs => docs.filter(d => d.id !== selectedDoc.id));
    notify(t.notifications.deleted, { type: 'success' });
    setDeleteDialog(false);
    setSelectedDoc(null);
  };

  // Status chip rengi
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'signed': return 'success';
      case 'rejected': return 'error';
      case 'viewed': return 'info';
      case 'draft': return 'default';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'signed': return <CheckCircleIcon />;
      case 'rejected': return <CancelIcon />;
      case 'viewed': return <VisibilityIcon />;
      case 'draft': return <EditIcon />;
      case 'archived': return <ArchiveIcon />;
      default: return <DescriptionIcon />;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: COLORS.background,
      pb: isMobile ? 10 : 6
    }}>
      {/* Header */}
      <GradientHeader elevation={0}>
        <Container maxWidth="xl">
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Typography variant="h4" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon sx={{ fontSize: 32 }} />
                  {t.pageTitle}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                  {t.pageSubtitle}
                </Typography>
              </motion.div>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title={t.refresh}>
                <IconButton 
                  onClick={() => {
                    setRefreshing(true);
                    setTimeout(() => setRefreshing(false), 1000);
                  }}
                  sx={{ 
                    color: 'white', 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/evraklarim/sablon-ekle')}
                sx={{
                  bgcolor: 'white',
                  color: COLORS.primary,
                  fontWeight: 700,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                }}
              >
                {t.uploadTemplate}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </GradientHeader>

      <Container maxWidth="xl" sx={{ mt: -4, position: 'relative', zIndex: 10 }}>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary }}>
                    <FolderIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{t.stats.total}</Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? <Skeleton width={40} /> : stats.total}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: alpha(COLORS.warning, 0.1), color: COLORS.warning }}>
                    <PendingIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{t.stats.pending}</Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? <Skeleton width={40} /> : stats.pending}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: alpha(COLORS.success, 0.1), color: COLORS.success }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{t.stats.completed}</Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? <Skeleton width={40} /> : stats.completed}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={6} sm={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: alpha(COLORS.info, 0.1), color: COLORS.info }}>
                    <EditIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{t.stats.draft}</Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? <Skeleton width={40} /> : stats.draft}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 4,
            bgcolor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: `1px solid ${COLORS.border}`
          }}
        >
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={2} 
            alignItems="center"
          >
            <TextField
              fullWidth
              size="small"
              placeholder={t.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: COLORS.text.hint }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              sx={{
                minWidth: 120,
                borderRadius: 3,
                borderColor: COLORS.border,
                color: COLORS.text.primary,
                '&:hover': {
                  borderColor: COLORS.primary,
                  bgcolor: alpha(COLORS.primary, 0.02)
                }
              }}
            >
              {t.filter}
            </Button>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Paper
          sx={{
            mb: 3,
            borderRadius: 4,
            bgcolor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minHeight: 60,
                fontWeight: 600,
                color: COLORS.text.secondary,
                '&.Mui-selected': {
                  color: COLORS.primary
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: COLORS.primary,
                height: 3
              }
            }}
          >
            <Tab label={t.tabs.all} />
            <Tab 
              label={
                <Badge badgeContent={stats.pending} color="warning" sx={{ '& .MuiBadge-badge': { right: -8 } }}>
                  {t.tabs.pending}
                </Badge>
              } 
            />
            <Tab label={t.tabs.completed} />
            <Tab label={t.tabs.draft} />
            <Tab label={t.tabs.archived} />
          </Tabs>
        </Paper>

        {/* Document Grid */}
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton 
                  variant="rectangular" 
                  height={200} 
                  sx={{ borderRadius: 4 }} 
                />
              </Grid>
            ))}
          </Grid>
        ) : filteredDocs.length === 0 ? (
          <Fade in>
            <Paper
              sx={{
                p: 8,
                borderRadius: 4,
                bgcolor: 'white',
                textAlign: 'center',
                border: `1px solid ${COLORS.border}`
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 100 }}
              >
                <FolderOpenIcon sx={{ fontSize: 80, color: COLORS.text.hint, mb: 2 }} />
              </motion.div>
              <Typography variant="h6" color="text.primary" gutterBottom>
                {t.empty.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t.empty.description}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/evraklarim/sablon-ekle')}
                sx={{
                  bgcolor: COLORS.primary,
                  '&:hover': { bgcolor: COLORS.secondary }
                }}
              >
                {t.empty.action}
              </Button>
            </Paper>
          </Fade>
        ) : (
          <Grid container spacing={2}>
            <AnimatePresence>
              {filteredDocs.map((doc, index) => (
                <Grid item xs={12} sm={6} md={4} key={doc.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <AnimatedCard
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 48,
                                height: 48,
                                bgcolor: alpha(COLORS.primary, 0.1),
                                color: COLORS.primary
                              }}
                            >
                              <PdfIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: 150 }}>
                                {doc.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {doc.pages} {t.card.pages} • {doc.size}
                              </Typography>
                            </Box>
                          </Stack>

                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, doc)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Stack>

                        {/* Description */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40 }} noWrap>
                          {doc.description}
                        </Typography>

                        {/* Tags */}
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          {doc.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{
                                bgcolor: alpha(COLORS.primary, 0.05),
                                color: COLORS.primary,
                                borderRadius: 1.5
                              }}
                            />
                          ))}
                        </Stack>

                        {/* Info */}
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarIcon sx={{ fontSize: 16, color: COLORS.text.hint }} />
                            <Typography variant="caption" color="text.secondary">
                              {t.card.created}: {format(new Date(doc.createdAt), 'dd.MM.yyyy')}
                            </Typography>
                          </Stack>

                          {doc.deadline && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <TimeIcon sx={{ fontSize: 16, color: COLORS.text.hint }} />
                              <Typography variant="caption" color="text.secondary">
                                {t.card.deadline}: {format(new Date(doc.deadline), 'dd.MM.yyyy')}
                              </Typography>
                            </Stack>
                          )}

                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PersonIcon sx={{ fontSize: 16, color: COLORS.text.hint }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t.card.sender}: {doc.sender}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Recipients */}
                        {doc.recipients.length > 0 && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 28, height: 28 } }}>
                              {doc.recipients.map((r, i) => (
                                <Tooltip key={i} title={`${r.name} - ${t.status[r.status]}`}>
                                  <Avatar
                                    sx={{
                                      bgcolor: r.status === 'signed' ? COLORS.success :
                                              r.status === 'pending' ? COLORS.warning :
                                              r.status === 'viewed' ? COLORS.info : COLORS.primary
                                    }}
                                  >
                                    {r.name.charAt(0)}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                            <Typography variant="caption" color="text.secondary">
                              +{doc.recipients.length} {t.card.more}
                            </Typography>
                          </Stack>
                        )}

                        {/* Footer */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <StatusChip
                            size="small"
                            icon={getStatusIcon(doc.status)}
                            label={t.status[doc.status]}
                            statuscolor={getStatusColor(doc.status)}
                          />

                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t.card.view}>
                              <ActionButton size="small" onClick={() => handleView(doc)}>
                                <VisibilityIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                            <Tooltip title={t.card.download}>
                              <ActionButton size="small" onClick={() => handleDownload(doc)}>
                                <DownloadIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                            <Tooltip title={t.card.share}>
                              <ActionButton size="small" onClick={() => handleShare(doc)}>
                                <ShareIcon fontSize="small" />
                              </ActionButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </AnimatedCard>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}
      </Container>

      {/* Floating Action Button */}
      <Zoom in={!loading}>
        <FloatingActionButton
          onClick={() => navigate('/evraklarim/sablon-ekle')}
          aria-label="new document"
        >
          <AddIcon />
        </FloatingActionButton>
      </Zoom>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            mt: 1,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => handleView(selectedDoc)}>
          <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: COLORS.primary }} /></ListItemIcon>
          <ListItemText primary={t.card.view} />
        </MenuItem>
        <MenuItem onClick={() => handleDownload(selectedDoc)}>
          <ListItemIcon><DownloadIcon fontSize="small" sx={{ color: COLORS.primary }} /></ListItemIcon>
          <ListItemText primary={t.card.download} />
        </MenuItem>
        <MenuItem onClick={() => handleShare(selectedDoc)}>
          <ListItemIcon><ShareIcon fontSize="small" sx={{ color: COLORS.primary }} /></ListItemIcon>
          <ListItemText primary={t.card.share} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDelete(selectedDoc)} sx={{ color: COLORS.error }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: COLORS.error }} /></ListItemIcon>
          <ListItemText primary="Sil" />
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <WarningIcon sx={{ fontSize: 48, color: COLORS.error, mb: 1 }} />
          <Typography variant="h6">{t.dialog.delete.title}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" color="text.secondary">
            {t.dialog.delete.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialog(false)}
            sx={{ borderRadius: 3, px: 4 }}
          >
            {t.dialog.delete.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            sx={{
              bgcolor: COLORS.error,
              '&:hover': { bgcolor: '#b71c1c' },
              borderRadius: 3,
              px: 4
            }}
          >
            {t.dialog.delete.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
            maxWidth: 500,
            width: '100%'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t.dialog.share.title}</Typography>
            <IconButton onClick={() => setShareDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={t.dialog.share.email}
              type="email"
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 3 }
              }}
            />
            <TextField
              fullWidth
              label={t.dialog.share.message}
              multiline
              rows={4}
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 3 }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              notify(t.notifications.shared, { type: 'success' });
              setShareDialog(false);
            }}
            sx={{
              bgcolor: COLORS.primary,
              '&:hover': { bgcolor: COLORS.secondary },
              borderRadius: 3,
              py: 1.5
            }}
          >
            {t.dialog.share.send}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Animasyon stilleri */}
      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}